import React, {memo, useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Popover, PopoverTrigger} from '../Popover';
import TooltipContent from './TooltipContent';

/**
 * Tooltip - Displays a tooltip pointing to the trigger element
 * Opens on hover by default
 *
 * @typedef {Object} TooltipProps
 * @property {React.ReactElement} trigger - The element that triggers the tooltip
 * @property {React.ReactNode} children - Content to display in the tooltip
 * @property {boolean} [defaultOpen] - Initial open state
 * @property {boolean} [open] - Controlled open state
 * @property {function} [onOpenChange] - Callback when open state changes
 * @property {string} [placement] - Popper placement ('top', 'bottom', 'left', 'right', etc.)
 * @property {string} [className] - Additional CSS classes for container
 * @property {('xs'|'sm'|'md'|'lg')} [size] - Size variant (default: 'md')
 * @property {boolean} [closeOnOutsideClick] - Whether to close on outside click (default: false for tooltips)
 * @property {boolean} [closeOnEscape] - Whether to close on Escape key (default: true)
 * @property {Array<number>} [offset] - Custom offset [x, y] for popper positioning (default: [0, 8])
 * @property {number} [delay] - Delay in milliseconds before opening the tooltip (default: 100)
 */
const Tooltip = memo(
	({
		trigger,
		children,
		defaultOpen = false,
		open: controlledOpen,
		onOpenChange,
		placement = 'top',
		className,
		size = 'md',
		closeOnOutsideClick = false,
		closeOnEscape = true,
		offset = [0, 8],
		delay = 500,
		...props
	}) => {
		const [internalOpen, setInternalOpen] = useState(defaultOpen);
		const hoverTimeoutRef = React.useRef(null);

		const isControlled = controlledOpen !== undefined;
		const open = isControlled ? controlledOpen : internalOpen;

		const setOpen = useCallback(
			value => {
				if (!isControlled) {
					setInternalOpen(value);
				}
				onOpenChange?.(value);
			},
			[isControlled, onOpenChange],
		);

		// Clear timeout on unmount
		useEffect(() => {
			return () => {
				if (hoverTimeoutRef.current) {
					clearTimeout(hoverTimeoutRef.current);
				}
			};
		}, []);

		// Handle hover enter with delay
		const handleMouseEnter = useCallback(() => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			if (delay > 0) {
				hoverTimeoutRef.current = setTimeout(() => {
					setOpen(true);
				}, delay);
			} else {
				setOpen(true);
			}
		}, [setOpen, delay]);

		// Handle hover leave with small delay to prevent flickering
		const handleMouseLeave = useCallback(() => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			hoverTimeoutRef.current = setTimeout(() => {
				setOpen(false);
			}, 100);
		}, [setOpen]);

		// Close tooltip when hovering over it
		const handleContentMouseEnter = useCallback(() => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			setOpen(false);
		}, [setOpen]);

		const handleContentMouseLeave = useCallback(() => {
			// No-op since we close on enter
		}, []);

		// Wrap trigger with hover handlers
		const triggerWithHover = React.useMemo(() => {
			if (!React.isValidElement(trigger)) {
				return trigger;
			}

			return React.cloneElement(trigger, {
				onMouseEnter: e => {
					trigger.props.onMouseEnter?.(e);
					handleMouseEnter();
				},
				onMouseLeave: e => {
					trigger.props.onMouseLeave?.(e);
					handleMouseLeave();
				},
			});
		}, [trigger, handleMouseEnter, handleMouseLeave]);

		return (
			<Popover
				open={open}
				onOpenChange={setOpen}
				placement={placement}
				className={className}
				closeOnOutsideClick={closeOnOutsideClick}
				closeOnEscape={closeOnEscape}
				offset={offset}
				{...props}
			>
				<PopoverTrigger asChild>{triggerWithHover}</PopoverTrigger>
				<TooltipContent
					size={size}
					onMouseEnter={handleContentMouseEnter}
					onMouseLeave={handleContentMouseLeave}
				>
					{children}
				</TooltipContent>
			</Popover>
		);
	},
);

Tooltip.propTypes = {
	trigger: PropTypes.element.isRequired,
	children: PropTypes.node.isRequired,
	defaultOpen: PropTypes.bool,
	open: PropTypes.bool,
	onOpenChange: PropTypes.func,
	placement: PropTypes.string,
	className: PropTypes.string,
	size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
	closeOnOutsideClick: PropTypes.bool,
	closeOnEscape: PropTypes.bool,
	offset: PropTypes.arrayOf(PropTypes.number),
	delay: PropTypes.number,
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
